import React, { useEffect, useMemo, useState, forwardRef, useImperativeHandle, useCallback } from 'react';
import { Form, Input, Select, DatePicker, Tabs, Button, Space } from 'antd';
import dayjs from 'dayjs';
import { useHrOptions } from '../../../hooks/useHrOptions';
import EmployeeDeptOrgTreeSelect from '../../../components/Org/EmployeeDeptOrgTreeSelect';
import { parseIdCardFront, ID_CARD_PATTERN } from '../../../utils/idCardUtils';
import {
  GENDER_OPTIONS, EMPLOYEE_TYPES, PROBATION_STATUS, EDUCATION_OPTIONS,
  CONTRACT_TYPES, EMPLOYMENT_STATUS, CHINA_ETHNICITIES
} from '../../../config/hrConstants';

const DATE_FIELDS = [
  'birth_date', 'hire_date', 'regularization_date', 'graduation_date',
  'first_contract_start', 'first_contract_end', 'current_contract_start', 'current_contract_end',
  'social_insurance_date', 'injury_insurance_date', 'employer_liability_date'
];

const ETHNICITY_OPTIONS = CHINA_ETHNICITIES.map((name) => ({ value: name, label: name }));

function formatEmployeePayload(values, certificates) {
  const payload = { ...values, certificates };
  DATE_FIELDS.forEach((f) => {
    if (payload[f]?.format) payload[f] = payload[f].format('YYYY-MM-DD');
  });
  return payload;
}

const EmployeeForm = forwardRef(({
  employee, stockMode = false, onboardingMode = false, onSuccess, onCancel, readOnly = false,
  embedded = false, onChange, mobileRequired = false
}, ref) => {
  const [form] = Form.useForm();
  const { companies, positions, employees, loadCompanies, loadLevels, loadEmployees } = useHrOptions();
  const [levels, setLevels] = useState([]);
  const [certificates, setCertificates] = useState([]);

  const directManagerOptions = useMemo(() => (
    employees
      .filter((e) => !employee?.employee_no || e.employee_no !== employee.employee_no)
      .map((e) => ({ value: e.employee_no, label: `${e.real_name}（${e.employee_no}）` }))
  ), [employees, employee?.employee_no]);

  const handleResetDirectManager = () => {
    onSuccess({ direct_manager_reset: true });
  };

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  useEffect(() => {
    if (!onboardingMode) {
      loadEmployees();
    }
  }, [loadEmployees, onboardingMode]);

  useEffect(() => {
    loadLevels().then(setLevels);
  }, [loadLevels]);

  useEffect(() => {
    if (employee) {
      const values = {
        ...employee,
        subsidiary_company_id: employee.subsidiary_company_id || employee.subsidiary_org_id || employee.subsidiary_org_code,
        dept_org_ids: employee.dept_org_ids?.length
          ? employee.dept_org_ids
          : (employee.dept_org_id || employee.dept_org_code ? [employee.dept_org_id || employee.dept_org_code] : [])
      };
      ['birth_date', 'hire_date', 'regularization_date', 'graduation_date',
        'first_contract_start', 'first_contract_end', 'current_contract_start', 'current_contract_end',
        'social_insurance_date', 'injury_insurance_date', 'employer_liability_date'].forEach((f) => {
        if (values[f]) values[f] = dayjs(values[f]);
      });
      form.setFieldsValue(values);
      setCertificates(employee.certificates || []);
    } else {
      form.resetFields();
      form.setFieldsValue({
        employment_status: 1,
        probation_status: stockMode ? '已转正' : '试用中',
        archive_status: 1
      });
      setCertificates([]);
    }
  }, [employee, form, stockMode, onboardingMode]);

  const applyIdCardDerived = useCallback((idCard) => {
    const parsed = parseIdCardFront(idCard);
    if (parsed) {
      form.setFieldsValue({
        birth_date: dayjs(parsed.birthDate),
        gender: parsed.gender
      });
    } else if (onboardingMode) {
      form.setFieldsValue({ birth_date: undefined, gender: undefined });
    }
  }, [form, onboardingMode]);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    onSuccess(formatEmployeePayload(values, certificates));
  };

  const buildPayload = useCallback(async () => {
    const values = await form.validateFields();
    return formatEmployeePayload(values, certificates);
  }, [form, certificates]);

  const buildDraftPayload = useCallback(() => {
    const values = form.getFieldsValue(true);
    return formatEmployeePayload(values, certificates);
  }, [form, certificates]);

  useImperativeHandle(ref, () => ({
    getPayload: buildPayload,
    getDraftPayload: buildDraftPayload
  }), [buildPayload, buildDraftPayload]);

  const handleValuesChange = (changed, allValues) => {
    if (onboardingMode && Object.prototype.hasOwnProperty.call(changed, 'id_card')) {
      applyIdCardDerived(changed.id_card);
    }
    if (embedded && onChange) {
      onChange(formatEmployeePayload(allValues, certificates));
    }
  };

  const idCardRules = onboardingMode ? [
    { required: true, message: '请输入身份证号' },
    { pattern: ID_CARD_PATTERN, message: '身份证号须为18位有效号码' }
  ] : [];

  const basicFields = onboardingMode ? (
    <>
      <Form.Item name="real_name" label="员工姓名" rules={[{ required: true, message: '请输入员工姓名' }]}>
        <Input />
      </Form.Item>
      <Form.Item name="id_card" label="身份证号码" rules={idCardRules}>
        <Input maxLength={18} onBlur={(e) => applyIdCardDerived(e.target.value)} />
      </Form.Item>
      <Form.Item name="gender" label="性别">
        <Select allowClear disabled options={GENDER_OPTIONS} />
      </Form.Item>
      <Form.Item name="birth_date" label="出生日期">
        <DatePicker style={{ width: '100%' }} disabled />
      </Form.Item>
      <Form.Item name="ethnicity" label="民族">
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="请选择民族"
          options={ETHNICITY_OPTIONS}
        />
      </Form.Item>
      <Form.Item
        name="mobile"
        label="手机号"
        rules={[
          ...(mobileRequired ? [{ required: true, message: '请输入手机号' }] : []),
          {
            validator: (_, value) => {
              const text = value != null ? String(value).trim() : '';
              if (!text) return Promise.resolve();
              if (!/^1[3-9]\d{9}$/.test(text)) {
                return Promise.reject(new Error('手机号格式不正确'));
              }
              return Promise.resolve();
            }
          }
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item name="email" label="邮箱"><Input /></Form.Item>
      <Form.Item name="subsidiary_company_id" label="所在公司" rules={[{ required: true, message: '请选择公司' }]}>
        <Select
          showSearch
          optionFilterProp="label"
          options={companies.map((c) => ({ value: c.id, label: c.label || c.company_name }))}
        />
      </Form.Item>
      <Form.Item
        name="dept_org_ids"
        label="所在部门/项目"
        rules={[{ required: true, type: 'array', min: 1, message: '请至少选择一个部门或项目' }]}
      >
        <EmployeeDeptOrgTreeSelect multiple />
      </Form.Item>
      <Form.Item name="hire_date" label="入职时间">
        <DatePicker style={{ width: '100%' }} />
      </Form.Item>
      <Form.Item name="position_level_id" label="岗位职级">
        <Select allowClear options={levels.map((l) => ({ value: l.id, label: l.level_name }))} />
      </Form.Item>
      <Form.Item name="employee_type" label="员工类型">
        <Select allowClear options={EMPLOYEE_TYPES.map((v) => ({ value: v, label: v }))} />
      </Form.Item>
    </>
  ) : (
    <>
      <Form.Item name="real_name" label="员工姓名" rules={[{ required: true }]}><Input /></Form.Item>
      <Form.Item name="id_card" label="身份证号码"><Input maxLength={18} /></Form.Item>
      <Form.Item name="gender" label="性别"><Select allowClear options={GENDER_OPTIONS} /></Form.Item>
      <Form.Item name="birth_date" label="出生日期"><DatePicker style={{ width: '100%' }} /></Form.Item>
      <Form.Item name="ethnicity" label="民族">
        <Select allowClear showSearch optionFilterProp="label" options={ETHNICITY_OPTIONS} />
      </Form.Item>
      <Form.Item
        name="mobile"
        label="手机号"
        rules={[
          ...(mobileRequired ? [{ required: true, message: '请输入手机号' }] : []),
          {
            validator: (_, value) => {
              const text = value != null ? String(value).trim() : '';
              if (!text) return Promise.resolve();
              if (!/^1[3-9]\d{9}$/.test(text)) {
                return Promise.reject(new Error('手机号格式不正确'));
              }
              return Promise.resolve();
            }
          }
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item name="email" label="邮箱"><Input /></Form.Item>
      <Form.Item name="remark" label="备注"><Input.TextArea rows={2} /></Form.Item>
    </>
  );

  const orgFields = !onboardingMode ? (
    <>
      <Form.Item name="cost_center" label="成本费用归属中心"><Input /></Form.Item>
      <Form.Item name="subsidiary_company_id" label="所在公司" rules={[{ required: true, message: '请选择公司' }]}>
        <Select showSearch optionFilterProp="label"
          options={companies.map((c) => ({ value: c.id, label: c.label || c.company_name }))} />
      </Form.Item>
      <Form.Item
        name="dept_org_ids"
        label="所在部门/项目"
        rules={[{ required: true, type: 'array', min: 1, message: '请至少选择一个部门或项目' }]}
      >
        <EmployeeDeptOrgTreeSelect multiple />
      </Form.Item>
      <Form.Item name="region" label="所在区域"><Input /></Form.Item>
      <Form.Item name="cost_company" label="成本归属公司"><Input /></Form.Item>
      <Form.Item name="hire_date" label="入职时间"><DatePicker style={{ width: '100%' }} /></Form.Item>
      <Form.Item name="position_id" label="岗位">
        <Select allowClear options={positions.map((p) => ({ value: p.id, label: p.label || p.position_name }))} />
      </Form.Item>
      <Form.Item name="position_level_id" label="岗位职级">
        <Select allowClear options={levels.map((l) => ({ value: l.id, label: l.level_name }))} />
      </Form.Item>
      <Form.Item name="employee_type" label="员工类型"><Select allowClear options={EMPLOYEE_TYPES.map((v) => ({ value: v, label: v }))} /></Form.Item>
      <Form.Item name="employment_status" label="在岗状态"><Select options={EMPLOYMENT_STATUS} /></Form.Item>
      <Form.Item name="probation_status" label="试用/转正状态"><Select options={PROBATION_STATUS.map((v) => ({ value: v, label: v }))} /></Form.Item>
      <Form.Item name="regularization_date" label="转正日期"><DatePicker style={{ width: '100%' }} /></Form.Item>
      {readOnly ? (
        <Form.Item label="直接上级">
          <Input
            readOnly
            value={
              employee?.direct_manager_name
                ? `${employee.direct_manager_name}（${employee.direct_manager_employee_no || ''}）`
                : (employee?.direct_manager_manual ? '未设置（手工）' : '未设置')
            }
          />
        </Form.Item>
      ) : (
        <>
          <Form.Item name="direct_manager_employee_no" label="直接上级">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="留空则按组织规则自动计算"
              options={directManagerOptions}
            />
          </Form.Item>
          {employee?.default_direct_manager?.direct_manager_name && (
            <div style={{ marginTop: -12, marginBottom: 16, color: '#888', fontSize: 12 }}>
              系统默认：{employee.default_direct_manager.direct_manager_name}
              （{employee.default_direct_manager.direct_manager_employee_no}）
              {employee.direct_manager_manual ? ' · 当前为手工指定' : ''}
            </div>
          )}
          {employee?.employee_no && (
            <Button size="small" onClick={handleResetDirectManager} style={{ marginBottom: 16 }}>
              恢复系统默认
            </Button>
          )}
        </>
      )}
    </>
  ) : null;

  const tabItems = [
    {
      key: 'basic',
      label: '基本信息',
      forceRender: embedded,
      children: basicFields
    },
    ...(!onboardingMode ? [{
      key: 'org',
      label: '归属与岗位',
      forceRender: embedded,
      children: orgFields
    }] : []),
    {
      key: 'edu',
      label: '教育与资质',
      children: (
        <>
          <Form.Item name="graduation_date" label="毕业时间"><DatePicker style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="major" label="所学专业"><Input /></Form.Item>
          <Form.Item name="education" label="学历"><Select allowClear options={EDUCATION_OPTIONS.map((v) => ({ value: v, label: v }))} /></Form.Item>
          <Form.Item name="degree" label="学位"><Input /></Form.Item>
          <Space style={{ marginBottom: 8 }}>
            <strong>资质证书</strong>
            {!readOnly && (
              <Button size="small" onClick={() => setCertificates([...certificates, { cert_name: '', cert_no: '', expire_date: '' }])}>添加证书</Button>
            )}
          </Space>
          {readOnly ? (
            certificates.length ? certificates.map((c, idx) => (
              <div key={idx} style={{ marginBottom: 8, color: '#555' }}>
                {[c.cert_name, c.cert_no, c.expire_date].filter(Boolean).join(' · ') || '-'}
              </div>
            )) : <div style={{ color: '#999' }}>暂无</div>
          ) : certificates.map((c, idx) => (
            <Space key={idx} style={{ display: 'flex', marginBottom: 8 }}>
              <Input placeholder="证书名称" value={c.cert_name} onChange={(e) => { const n = [...certificates]; n[idx].cert_name = e.target.value; setCertificates(n); }} />
              <Input placeholder="证书编号" value={c.cert_no} onChange={(e) => { const n = [...certificates]; n[idx].cert_no = e.target.value; setCertificates(n); }} />
              <Input placeholder="有效期 YYYY-MM-DD" value={c.expire_date} onChange={(e) => { const n = [...certificates]; n[idx].expire_date = e.target.value; setCertificates(n); }} />
            </Space>
          ))}
        </>
      )
    },
    {
      key: 'contract',
      label: '合同与银行',
      children: (
        <>
          <Form.Item name="contract_company" label="合同所属公司"><Input /></Form.Item>
          <Form.Item name="contract_type" label="合同类型"><Select allowClear options={CONTRACT_TYPES.map((v) => ({ value: v, label: v }))} /></Form.Item>
          <Form.Item name="first_contract_start" label="首次合同起始日"><DatePicker style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="first_contract_end" label="首次合同到期日"><DatePicker style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="current_contract_start" label="现合同起始日"><DatePicker style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="current_contract_end" label="现合同到期日"><DatePicker style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="contract_term" label="合同期限"><Input /></Form.Item>
          <Form.Item name="renewal_count" label="续签次数"><Input type="number" /></Form.Item>
          <Form.Item name="bank_account_name" label="银行卡姓名"><Input /></Form.Item>
          <Form.Item name="bank_account_no" label="银行卡账号"><Input /></Form.Item>
        </>
      )
    },
    {
      key: 'insurance',
      label: '社保与保险',
      children: (
        <>
          <Form.Item name="social_insurance_status" label="社保五险状态"><Input /></Form.Item>
          <Form.Item name="social_three_status" label="社保三险状态"><Input /></Form.Item>
          <Form.Item name="social_insurance_date" label="社保购买日期"><DatePicker style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="injury_insurance_status" label="超龄工伤险状态"><Input /></Form.Item>
          <Form.Item name="injury_insurance_date" label="超龄工伤险购买日期"><DatePicker style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="employer_liability_status" label="雇主责任险状态"><Input /></Form.Item>
          <Form.Item name="employer_liability_date" label="雇主责任险购买日期"><DatePicker style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="insurer_name" label="承保保险公司"><Input /></Form.Item>
        </>
      )
    },
    {
      key: 'emergency',
      label: '紧急联系人',
      children: (
        <>
          <Form.Item name="emergency_contact_name" label="紧急联系人姓名"><Input /></Form.Item>
          <Form.Item name="emergency_contact_relation" label="与本人关系"><Input /></Form.Item>
          <Form.Item name="emergency_contact_phone" label="联系电话"><Input /></Form.Item>
        </>
      )
    }
  ];

  return (
    <Form
      form={form}
      layout="vertical"
      disabled={readOnly}
      className={readOnly ? 'process-form-readonly' : undefined}
      onValuesChange={handleValuesChange}
    >
      <Tabs items={tabItems} destroyInactiveTabPane={false} />
      {!readOnly && !embedded && (
        <div style={{ textAlign: 'right', marginTop: 16 }}>
          <Space>
            <Button onClick={onCancel}>取消</Button>
            <Button type="primary" onClick={handleSubmit}>保存</Button>
          </Space>
        </div>
      )}
    </Form>
  );
});

EmployeeForm.displayName = 'EmployeeForm';

export default EmployeeForm;
